import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { DollarSign, RefreshCw, PenLine } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

/**
 * Per-row "Fix Amount" action: tries to backfill from Stripe first; if that
 * fails (mode mismatch / deleted PI / no PI on record), opens a manual
 * amount-entry dialog. Keeps the admin in flow without leaving the row.
 *
 * Use on any paid-with-$0.00 row in lane bookings, tournament registrations,
 * or payment transactions.
 */
const RowAmountFixerImpl = ({ entityType, entityId, currentAmount, onUpdated, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualReason, setManualReason] = useState('');  // surfaced from backend if Stripe failed
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  // Try Stripe first.
  const tryStripeBackfill = async (e) => {
    if (e) e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post('/admin/payments/backfill-row', {
        entity_type: entityType,
        entity_id: entityId,
      });
      // Backend now returns 200 with `ok: false` for soft failures (no PI,
      // mode mismatch, etc.) so the network panel stays clean. Old shape
      // (HTTPException → caught below) is still handled for compatibility.
      if (r.data?.ok === false) {
        const code = r.data?.code;
        if (code === 'stripe_amount_unavailable' || code === 'no_payment_intent') {
          const mode = r.data.stripe_mode ? ` (Stripe mode: ${r.data.stripe_mode})` : '';
          setManualReason((r.data.message || '') + mode);
          setManualOpen(true);
        } else {
          toast.error(r.data?.message || 'Backfill failed');
        }
        return;
      }
      toast.success(r.data?.message || 'Amount restored from Stripe');
      onUpdated && onUpdated(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Stripe couldn't help → fall back to manual entry, prefilling the
      // reason so the admin understands why.
      if (typeof detail === 'object' && (detail?.code === 'stripe_amount_unavailable' || detail?.code === 'no_payment_intent')) {
        const mode = detail.stripe_mode ? ` (Stripe mode: ${detail.stripe_mode})` : '';
        setManualReason((detail.message || '') + mode);
        setManualOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Backfill failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    const amt = Number.parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount > 0');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/admin/payments/manual-amount', {
        entity_type: entityType,
        entity_id: entityId,
        amount: amt,
        note: note.trim() || undefined,
      });
      toast.success(r.data?.message || 'Amount set');
      setManualOpen(false);
      setAmount('');
      setNote('');
      onUpdated && onUpdated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set amount');
    } finally {
      setBusy(false);
    }
  };

  const openManualDirectly = (e) => {
    if (e) e.stopPropagation();
    setManualReason('');
    setAmount(currentAmount && currentAmount > 0 ? String(currentAmount) : '');
    setManualOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button
          size={compact ? 'sm' : 'default'}
          variant="outline"
          onClick={tryStripeBackfill}
          disabled={busy}
          className={compact ? 'h-7 px-2 text-xs' : ''}
          data-testid={`row-fix-amount-stripe-${entityId}`}
          title="Try to restore the amount from Stripe"
        >
          {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
          {compact ? '' : <span className="ml-1">Fix from Stripe</span>}
        </Button>
        <Button
          size={compact ? 'sm' : 'default'}
          variant="ghost"
          onClick={openManualDirectly}
          disabled={busy}
          className={compact ? 'h-7 px-2 text-xs text-slate-500' : 'text-slate-500'}
          data-testid={`row-fix-amount-manual-${entityId}`}
          title="Set the amount manually (use when Stripe can't help)"
        >
          <PenLine className="h-3 w-3" />
          {compact ? '' : <span className="ml-1">Manual</span>}
        </Button>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Set Payment Amount Manually</DialogTitle>
            <DialogDescription>
              {manualReason
                ? <span className="text-amber-700">{manualReason}</span>
                : 'Use this when Stripe can\'t return the amount (test/live mode mismatch, deleted PI, or offline/cash payment).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="manual-amount" className="text-sm">Amount (USD)</Label>
              <div className="relative">
                <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  id="manual-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-8"
                  data-testid="manual-amount-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="manual-note" className="text-sm">Note (optional)</Label>
              <Textarea
                id="manual-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Cash payment, mode mismatch, original Stripe receipt #..."
                rows={2}
                data-testid="manual-amount-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={submitManual}
              disabled={busy || !amount}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="manual-amount-save-btn"
            >
              {busy ? 'Saving…' : 'Save Amount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Retired from screens (iter392) — code kept.
export const RowAmountFixer = (props) => (RETIRED_TOOLS_VISIBLE ? <RowAmountFixerImpl {...props} /> : null);
